import type { DiagnosticSeverity } from '@aibi/semantic-model';
import { AppError, type ErrorDetail } from '@aibi/shared';

/** Codigos estables de diagnostico del planificador; la UI y la IA dependen de ellos, no del texto. */
export const ANALYSIS_DIAGNOSTIC_CODES = [
  /** El Semantic Model no cumple su contrato o sus reglas de integridad. */
  'invalid-model',
  /** El Analysis State no cumple su contrato o es incoherente (claves duplicadas, modelo distinto...). */
  'invalid-state',
  'missing-reference',
  'invalid-type',
  'invalid-aggregation',
  'invalid-expression',
  /** Mas de un camino de relaciones igual de corto entre dos tablas. */
  'ambiguous-relationship',
  /** No existe camino de relaciones activas hasta una tabla requerida. */
  'missing-relationship',
  'cyclic-dependency',
  /** Capacidad no expresable con los contratos o el AST actuales. */
  'unsupported',
  'invalid-filter',
  'invalid-pagination',
  /** Un join multiplica filas antes de agregar (uno a muchos). */
  'fan-out',
  /** Se anadieron criterios de desempate para que la paginacion sea estable. */
  'stable-order',
  /** Parte del estado que no se aplica (por ejemplo, drilldown desactivado). */
  'ignored',
  'empty-analysis',
] as const;
export type AnalysisDiagnosticCode = (typeof ANALYSIS_DIAGNOSTIC_CODES)[number];

/**
 * Diagnostico estructurado del planificador. Nunca incluye filas ni secretos: solo ids,
 * codigos y un mensaje legible. `path` es relativo al Analysis State salvo que empiece
 * por `model.`.
 */
export interface AnalysisDiagnostic {
  readonly severity: DiagnosticSeverity;
  readonly code: AnalysisDiagnosticCode;
  readonly message: string;
  readonly path?: string;
  readonly objectId?: string;
}

export const hasPlanErrors = (diagnostics: readonly AnalysisDiagnostic[]): boolean =>
  diagnostics.some((diagnostic) => diagnostic.severity === 'error');

export function planDiagnosticsToDetails(
  diagnostics: readonly AnalysisDiagnostic[],
): ErrorDetail[] {
  return diagnostics.map((diagnostic) => ({
    path: diagnostic.path ?? diagnostic.objectId ?? '',
    message: diagnostic.message,
  }));
}

/**
 * Error de planificacion: `VALIDATION_ERROR` (o `UNSUPPORTED` si todos los errores son
 * capacidades no soportadas) con `details` compatibles y los diagnosticos completos.
 *
 * @example
 * const plan = buildQueryPlan(state, model);
 * if (!plan.ok) plan.error.diagnostics.forEach(show);
 */
export class AnalysisPlanError extends AppError {
  readonly diagnostics: readonly AnalysisDiagnostic[];

  constructor(message: string, diagnostics: readonly AnalysisDiagnostic[]) {
    const errors = diagnostics.filter((diagnostic) => diagnostic.severity === 'error');
    const unsupportedOnly =
      errors.length > 0 && errors.every((diagnostic) => diagnostic.code === 'unsupported');
    super(unsupportedOnly ? 'UNSUPPORTED' : 'VALIDATION_ERROR', message, {
      details: planDiagnosticsToDetails(errors),
    });
    this.diagnostics = diagnostics;
  }
}

type DiagnosticExtras = Pick<AnalysisDiagnostic, 'path' | 'objectId'>;

/** Acumulador de diagnosticos en orden de emision (determinista). */
export class DiagnosticBag {
  readonly #items: AnalysisDiagnostic[] = [];

  get items(): readonly AnalysisDiagnostic[] {
    return this.#items;
  }

  get hasErrors(): boolean {
    return hasPlanErrors(this.#items);
  }

  add(diagnostic: AnalysisDiagnostic): void {
    this.#items.push(diagnostic);
  }

  addAll(diagnostics: readonly AnalysisDiagnostic[]): void {
    for (const diagnostic of diagnostics) this.#items.push(diagnostic);
  }

  error(code: AnalysisDiagnosticCode, message: string, extras: DiagnosticExtras = {}): void {
    this.add({ severity: 'error', code, message, ...extras });
  }

  warning(code: AnalysisDiagnosticCode, message: string, extras: DiagnosticExtras = {}): void {
    this.add({ severity: 'warning', code, message, ...extras });
  }

  info(code: AnalysisDiagnosticCode, message: string, extras: DiagnosticExtras = {}): void {
    this.add({ severity: 'info', code, message, ...extras });
  }
}
